/**
 * `<Combobox>` — фильтруемый комбобокс (триггер-кнопка + поповер с поиском).
 *
 * Завести для растущих списков сущностей (сотрудники, проекты, подразделения),
 * где обычный native `<select>` неудобен. Сборка shadcn: `Popover` + `Command`
 * (`cmdk`) — фильтрация по вводу, клавиатурная навигация и a11y из коробки.
 * Правило «когда combobox, когда native `<select>`» — `patterns.md
 * §Фильтруемый комбобокс` (порог ~10 / растущие сущности vs короткие
 * фиксированные списки).
 *
 * Видимый текст (placeholder, поиск, пустое состояние) приходит пропсами от
 * потребителя через i18n — внутри хардкода нет. Технический id опции
 * (`value`) не показываем, в триггере и списке рендерим `label`.
 */
import * as React from "react";
import { CheckIcon, ChevronsUpDownIcon, PlusIcon, XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { formControlChromeClassName } from "@/components/ui/form-control";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

export interface ComboboxOption {
  /** Стабильное значение опции (технический id — пользователю не показывается). */
  value: string;
  /** Видимая подпись опции. */
  label: string;
  /** Вторичная строка-подсказка под подписью (например, должность). */
  hint?: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  /** Выбранное значение или `null`, если ничего не выбрано. */
  value: string | null;
  onChange: (value: string | null) => void;
  /** Текст триггера, когда ничего не выбрано. */
  placeholder: string;
  /** Плейсхолдер поля поиска. */
  searchPlaceholder: string;
  /** Текст пустого состояния, когда ничего не найдено. */
  emptyText: string;
  /** Подпись кнопки очистки (a11y). Очистка скрыта, если не задана. */
  clearLabel?: string;
  /**
   * Завести новую опцию прямо из поповера (как лейблы в Jira). Задан вместе с
   * `createLabel` → при непустом вводе без точного совпадения в списке
   * появляется строка-действие. Потребитель сам создаёт сущность и выставляет
   * `value` — примитив только сообщает введённое имя.
   */
  onCreate?: (name: string) => void;
  /** Подпись строки-действия; получает введённое имя. */
  createLabel?: (name: string) => string;
  disabled?: boolean;
  className?: string;
  /** Ширина поповера; по умолчанию подгоняется под триггер. */
  contentClassName?: string;
  "data-testid"?: string;
  id?: string;
  ref?: React.Ref<HTMLButtonElement>;
  "aria-invalid"?: React.AriaAttributes["aria-invalid"];
  "aria-describedby"?: string;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  clearLabel,
  onCreate,
  createLabel,
  disabled,
  className,
  contentClassName,
  "data-testid": testId,
  id,
  ref,
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
}: ComboboxProps): React.ReactElement {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const selected = options.find((o) => o.value === value) ?? null;
  const showClear = clearLabel !== undefined && selected !== null;
  const typed = query.trim();
  // Точное совпадение регистрозависимо — так же, как канон имён в каталогах:
  // «Слесарь» и «слесарь» это разные подписи, и пользователь вправе завести обе.
  const canCreate =
    onCreate !== undefined &&
    createLabel !== undefined &&
    typed !== "" &&
    !options.some((o) => o.label === typed);

  return (
    <TooltipProvider>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next);
          if (!next) setQuery("");
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={id}
            ref={ref}
            type="button"
            variant="outline"
            size="default"
            role="combobox"
            aria-expanded={open}
            aria-invalid={ariaInvalid}
            aria-describedby={ariaDescribedBy}
            disabled={disabled}
            data-testid={testId}
            className={cn(
              formControlChromeClassName,
              "w-full justify-between font-normal",
              className,
            )}
          >
            {selected ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="min-w-0 truncate"
                    data-testid={testId ? `${testId}-selected-label` : undefined}
                  >
                    {selected.label}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{selected.label}</TooltipContent>
              </Tooltip>
            ) : (
              <span className="min-w-0 truncate text-muted-foreground">{placeholder}</span>
            )}
            <span className="flex shrink-0 items-center gap-0.5">
              {showClear && (
                <span
                  role="button"
                  tabIndex={0}
                  aria-label={clearLabel}
                  data-testid={testId ? `${testId}-clear` : undefined}
                  className="-mr-1 inline-flex size-4 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground"
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(null);
                    }
                  }}
                >
                  <XIcon className="size-3.5" aria-hidden />
                </span>
              )}
              <ChevronsUpDownIcon className="size-4 text-muted-foreground" aria-hidden />
            </span>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className={cn("w-(--radix-popover-trigger-width) p-0", contentClassName)}
        >
          <Command>
            <CommandInput placeholder={searchPlaceholder} value={query} onValueChange={setQuery} />
            <CommandList
              className="overscroll-contain"
              data-testid={testId ? `${testId}-list` : undefined}
            >
              {/* Пустое состояние уступает строке-действию: одновременно «ничего
                не найдено» и «создать …» — противоречивая пара. */}
              {!canCreate && <CommandEmpty>{emptyText}</CommandEmpty>}
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={`${option.label} ${option.hint ?? ""}`}
                  disabled={option.disabled}
                  data-testid={testId ? `${testId}-option-${option.value}` : undefined}
                  onSelect={() => {
                    onChange(option.value === value ? value : option.value);
                    setOpen(false);
                  }}
                  className="flex items-start gap-1.5"
                >
                  <CheckIcon
                    className={cn(
                      "mt-0.5 size-4 shrink-0",
                      option.value === value ? "opacity-100" : "opacity-0",
                    )}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-col">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="truncate"
                          data-testid={
                            testId ? `${testId}-option-${option.value}-label` : undefined
                          }
                        >
                          {option.label}
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>{option.label}</TooltipContent>
                    </Tooltip>
                    {option.hint && (
                      <span className="truncate text-xs text-muted-foreground">{option.hint}</span>
                    )}
                  </span>
                </CommandItem>
              ))}
              {/* Строка-действие идёт последней: сначала то, что уже есть в
                справочнике, и лишь потом «завести новое». */}
              {canCreate && (
                <CommandItem
                  forceMount
                  value={typed}
                  data-testid={testId ? `${testId}-create` : undefined}
                  onSelect={() => {
                    onCreate(typed);
                    setQuery("");
                    setOpen(false);
                  }}
                  className="items-start gap-1.5"
                >
                  <PlusIcon className="mt-0.5 size-4 shrink-0 text-muted-foreground" aria-hidden />
                  {/* Без truncate: служебная часть лейбла съедает узкий поповер и
                    режет именно введённое имя (аудит §4.1). Строка последняя в
                    списке — вертикальный рост безопасен. */}
                  <span className="break-words whitespace-normal">{createLabel(typed)}</span>
                </CommandItem>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </TooltipProvider>
  );
}
